import _ from 'lodash'
import moment from 'moment'
import * as sentry from '../../src/utils/sentry'
import { Logg_event } from '../Logg/Logg.event'
import type { NReports } from './Rpts.types'
import type { Type_ICalendarEvent } from '../Type/Type.hive'

/**
 * Called during sync to make sure that that a report has a calendar event with the right online id
 */
export function Rpts_ensureReportHasEvent(report: NReports.TIntermediateReport, events: Type_ICalendarEvent[]) {
    // Somehow we have ended up without an event property, we need to find a similar event to upload against
    // This should ideally never happen
    if (!report.event) {
        const potentialEvent = findMatchingEvent(report, events)

        if (potentialEvent) {
            sentry.err('Report upload: event inferred as no event property', {})
            report.event = potentialEvent
        } else {
            sentry.err('Report upload: no event property and no valid event found', {})
            return
        }
    }

    // most of the time the event upload logic handles updating the online id attached to the report event.
    // however there are race conditions where a new report can be missed, so we leave this in as a fallback.
    if (!report.event.onlineId) {
        let event = _.find(events, (e) => e.id === report.event.id)

        if (!event) {
            event = findMatchingEvent(report, events)

            if (!event) {
                sentry.err('Report upload: cached local event can not be found in local db', {
                    extra: { report: report.event },
                })
                return
            } else {
                sentry.err('Report upload: Found a matching event as local event does not exist', {
                    extra: { report: report.event },
                })
            }
        }

        report.event = event
    }
}

/**
 * Sometimes events go missing from reports. This is ongoing issue that we would like to fix.
 * Until we get to the bottom of it, this helps to keep reports syncing in the meantime.
 * The idea is that if the report is missing an event, look for an event that already exists
 * and take that to be the event that the report was for.
 * This is better than just creating a new events because duplicate event lead to user confusion
 * and one of the events that exists is likely to be the correct one.
 * The heuristic used here is that the correct event should have the same location and workflow,
 * and typically it will be on the same day, but that shouldn't stop us if there isn't one on the
 * same day because users might select to start a report on an event that isn't today.
 * Find an event is better than not finding one as this stops reports syncing which can lead to
 * data loss.
 */
function findMatchingEvent(report: NReports.TIntermediateReport, events: Type_ICalendarEvent[]) {
    const potentialEvents = events.filter(
        (event) => event.location === report.location && event.template === report.reportedActionList.actionList
    )

    if (potentialEvents.length > 0) {
        if (potentialEvents.length > 1) {
            Logg_event('FoundMultipleMatchingCalendarEvents', { count: potentialEvents.length })
            sentry.err('Found multiple matching events', { extra: { potentialEvents } })
        }

        // If multiple events, prefer one on the same day
        for (const event of potentialEvents) {
            if (moment(event.startDate).isSame(moment(report.startDate), 'day')) {
                return event
            }
        }

        return potentialEvents[0]
    }

    return undefined
}
